import Hls from "hls.js";

import { JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import Icon from "@/components/common/Icon/Icon.tsx";

import { cn } from "@/utils/cn.ts";

import "./style.css";

interface MovieBannerPlayerProps {
  src: string;
  videoSrc?: string | null;
  index: number;
  slideNum: number;
  onVideoEnd: () => void;
  loop?: boolean;
}

const MovieBannerPlayerMobile = ({ videoSrc, index, slideNum, src }: MovieBannerPlayerProps): JSX.Element => {
  const { t } = useTranslation();
  const { ref: divRef, inView } = useInView({
    threshold: 0.5,
  });
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isVideoShown, setIsVideoShown] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);

  const mobileVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (index === slideNum) {
      timeout = setTimeout(() => setIsVideoShown(true), 1000);
    } else {
      setIsVideoReady(false);
      setIsVideoShown(false);
      setIsMuted(true);
    }

    return () => clearTimeout(timeout);
  }, [index, slideNum]);

  useEffect(() => {
    const progressCircle = document.querySelector(`.progress-circle-${index}`) as SVGCircleElement;

    if (!videoSrc) return;

    if (mobileVideoRef.current && isVideoShown) {
      const video = mobileVideoRef.current;
      video.volume = 1;

      const isHlsSupported = Hls.isSupported();
      if (isHlsSupported) {
        const hls = new Hls();
        hls.loadSource(videoSrc);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, async () => {
          hls.levels.forEach((level, levelIndex) => {
            if (level.name === "480") {
              hls.currentLevel = levelIndex;
            }
          });
          await video.play();
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // iOS: использовать нативную поддержку
        video.src = videoSrc;
        video.addEventListener("loadedmetadata", async () => {
          try {
            await video.play();
          } catch (err) {
            console.error("Ошибка при воспроизведении на iOS:", err);
          }
        });
      }

      const circumference = 2 * Math.PI * 15;
      if (progressCircle) {
        progressCircle.style.strokeDasharray = `${circumference} ${circumference}`;
        progressCircle.style.strokeDashoffset = `${circumference}`;
        progressCircle.style.stroke = "#FF4B00";

        video.addEventListener("timeupdate", () => {
          const offset = (1 - video.currentTime / video.duration) * circumference;
          progressCircle.style.strokeDashoffset = offset.toString();
        });
      }
    }

    return () => {
      if (progressCircle) {
        progressCircle.style.strokeDashoffset = "0";
        progressCircle.style.stroke = "transparent";
      }
    };
  }, [index, videoSrc, isVideoShown, slideNum]);

  useEffect(() => {
    if (index === slideNum && mobileVideoRef.current && isVideoShown) {
      const video = mobileVideoRef.current;
      if (inView) {
        video.play();
      } else {
        video.pause();
      }
    }
  }, [index, isVideoShown, slideNum, inView]);

  return (
    <div ref={divRef} className={"relative aspect-[375/583] overflow-hidden"}>
      <div
        className="absolute left-0
       top-0 h-full w-full"
        style={{
          background: "linear-gradient(to top, var(--surface) 35%, transparent 80%)",
        }}
      ></div>
      {!videoSrc && <img src={src} className="h-full w-full object-cover" />}
      {index == slideNum && videoSrc != null && (
        <video
          ref={mobileVideoRef}
          muted={isMuted}
          loop={true}
          autoPlay={isVideoReady}
          playsInline
          poster={src}
          onCanPlay={() => {
            setIsVideoReady(true);
          }}
          onEnded={() => {
            setIsVideoReady(false);
          }}
          className={cn("h-full w-full object-cover object-top", {
            "object-center": isVideoReady,
          })}
        />
      )}
      <div className="absolute left-0 top-0 h-full w-full"></div>
      {index === slideNum && isVideoReady && (
        <button
          type={"button"}
          title={isMuted ? t("sound-on") : t("sound-off")}
          onClick={() => setIsMuted((prev) => !prev)}
          className={
            "button-appear absolute right-[3.625rem] top-8 z-[1] rounded-full border-2  border-on-primary p-2 py-3 max-xl:right-5 max-xl:top-5 max-lg:right-4 max-lg:top-4 max-lg:p-[6px] max-lg:py-[10px]"
          }
        >
          <Icon
            name={isMuted ? "muted" : "unmuted"}
            className={"h-4 w-6 fill-on-primary stroke-on-primary max-lg:h-3 max-lg:w-5"}
          />
        </button>
      )}
    </div>
  );
};

export default MovieBannerPlayerMobile;
